/**
 * Who may call this AI Service — the ONE editor for its access policy
 * (task 973).
 *
 * Report: `project/reports/innovate-app-service-access-policy-management.md`.
 *
 * The editor used to live only on the Composer page (`AuthPolicyPanel`), a page
 * whose subject is generating an interface rather than managing a service, and
 * it was gated behind the Composer's own flag — so with that flag off, access
 * could not be changed from the app at all. The logic moved here, to the AI
 * Service's Config tab. The Composer page and its panel were retired in task
 * 1132, so this is now the only mounting point.
 *
 * **No new enforcement semantics.** It saves through the same
 * `useUpdateEndpoint` call with an `authPolicy` payload the Composer panel
 * already made; innovate-service's middleware is unchanged.
 *
 * Two things the selector states that the old one did not:
 *   1. `custom` is not offered. The service answers a `custom`-mode call with
 *      `501 AUTH_MODE_UNAVAILABLE`, so putting it on the menu only sold a
 *      broken option. A row that already carries it keeps showing it.
 *   2. The origin allow-list governs EXTERNAL sites only — the platform's own
 *      app origins are always sanctioned by the service, for every mode.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyIcon, PlusIcon, ShieldCheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { FormSelect } from '@/components/shared/form/FormSelect';
import { useUpdateEndpoint } from '@/hooks/useEndpoints';
import { useAccountApiKeyCount } from '@/hooks/useApiKeys';
import { useToast } from '@/contexts/ToastContext';
import type { AuthPolicyMode, Endpoint } from '@/types';
import {
  ACCESS_MODE_SENTENCES,
  EDITABLE_ACCESS_MODES,
  apiKeyAccessSentence,
  serviceAccessMode,
} from './serviceAccess';

/** What each option promises, spelled out beside its label in the selector. */
const MODE_OPTION_LABELS: Record<AuthPolicyMode, string> = {
  public: 'Public — anyone can call it',
  apiKey: 'API key — callers need an account key',
  jwt: 'Signed in — only users of this account',
  custom: 'Custom (not available)',
};

interface ServiceAccessSectionProps {
  endpoint: Endpoint;
  /** Extra classes for the outer card, so a host can space it in its own layout. */
  className?: string;
}

export function ServiceAccessSection({ endpoint, className = '' }: ServiceAccessSectionProps) {
  const update = useUpdateEndpoint();
  const { success, error: toastError } = useToast();
  const savedMode = serviceAccessMode(endpoint);
  const [mode, setMode] = useState<AuthPolicyMode>(savedMode);
  const [origins, setOrigins] = useState<string[]>(endpoint.authPolicy?.origins ?? []);
  const [newOrigin, setNewOrigin] = useState('');
  /**
   * The account's key count, read ONLY while the API-key block is on screen
   * (task 980) — every other mode says nothing about keys, so it asks nothing.
   * `null` covers not-yet-answered and refused alike; the sentence then drops
   * the number rather than guessing one.
   */
  const activeApiKeyCount = useAccountApiKeyCount({ enabled: mode === 'apiKey' });

  // Re-sync when the service (or its stored policy) changes underneath us — the
  // Composer swaps the selected endpoint without remounting this panel.
  useEffect(() => {
    setMode(serviceAccessMode(endpoint));
    setOrigins(endpoint.authPolicy?.origins ?? []);
    setNewOrigin('');
  }, [endpoint.id, endpoint.authPolicy]);

  /**
   * `custom` is offered ONLY to a service already stored on it, so the selector
   * can show what the service is without becoming a way to choose it.
   */
  const modeOptions = (
    savedMode === 'custom' ? [...EDITABLE_ACCESS_MODES, 'custom' as const] : EDITABLE_ACCESS_MODES
  ).map((value) => ({ value, label: MODE_OPTION_LABELS[value] }));

  const addOrigin = () => {
    const trimmed = newOrigin.trim();
    if (!trimmed || origins.includes(trimmed)) return;
    setOrigins((prev) => [...prev, trimmed]);
    setNewOrigin('');
  };

  const removeOrigin = (origin: string) =>
    setOrigins((prev) => prev.filter((o) => o !== origin));

  const save = async () => {
    try {
      await update.mutateAsync({ id: endpoint.id, data: { authPolicy: { mode, origins } } });
      success('Access saved');
    } catch (err) {
      toastError(err instanceof Error ? err.message : 'Failed to save access');
    }
  };

  return (
    <section
      data-testid="service-access-section"
      data-mode={mode}
      className={`rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800 space-y-4 ${className}`}
    >
      <div>
        <h3 className="flex items-center text-sm font-semibold text-gray-900 dark:text-white">
          <ShieldCheckIcon className="mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
          Access
        </h3>
        <p
          className="mt-1 text-sm text-gray-500 dark:text-gray-400"
          data-testid="service-access-current"
        >
          {ACCESS_MODE_SENTENCES[mode]}
        </p>
      </div>

      <FormSelect
        label="Who can call this service"
        name="service-access-mode"
        value={mode}
        onChange={(e) => setMode(e.target.value as AuthPolicyMode)}
        options={modeOptions}
      />

      {/* The API-key mode's one follow-up question — where the keys are, and how
          many there are. Any active key of the account satisfies any
          API-key-mode service in it; there is no per-service scoping, so the
          count is the ACCOUNT's and matches what the API Keys page lists. */}
      {mode === 'apiKey' && (
        <p
          className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300"
          data-testid="service-access-api-keys"
        >
          <KeyIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
          <span>
            {apiKeyAccessSentence(activeApiKeyCount)}{' '}
            <Link
              to="/settings/api-keys"
              data-testid="service-access-api-keys-link"
              className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              Manage API keys
            </Link>
          </span>
        </p>
      )}

      <div>
        <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Allowed origins
        </span>
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          Browser calls from these exact origins are allowed. This app's own origins are always
          allowed, so the list is for external sites only — the page you copied the interface onto.
        </p>
        {origins.length === 0 ? (
          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
            No origins yet — a browser on another site cannot call this service until its origin is
            listed here.
          </p>
        ) : (
          <ul className="mb-2 space-y-1" data-testid="origin-list">
            {origins.map((origin) => (
              <li
                key={origin}
                className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-800 dark:border-gray-700 dark:text-gray-200"
              >
                <span className="truncate">{origin}</span>
                <button
                  type="button"
                  onClick={() => removeOrigin(origin)}
                  aria-label={`Remove ${origin}`}
                  className="text-gray-400 hover:text-red-500"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newOrigin}
            onChange={(e) => setNewOrigin(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addOrigin();
              }
            }}
            placeholder="https://app.example.com"
            aria-label="New origin"
            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
          <button
            type="button"
            onClick={addOrigin}
            disabled={!newOrigin.trim()}
            aria-label="Add origin"
            className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={update.isPending}
        data-testid="service-access-save"
        className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {update.isPending ? 'Saving…' : 'Save access'}
      </button>
    </section>
  );
}

export default ServiceAccessSection;
